import { useCallback, useEffect, useRef, useState } from 'react';
import { usePolling } from '@client/hooks/usePolling';
import { useFetchCompletion } from './use-fetch-completion';

const MAX_QUEUED_POLLS = 20;
const DEFAULT_MAX_POLLING_ATTEMPTS = 50;
const DEFAULT_POLLING_INTERVAL_MS = 2000;

interface UseRequestPollingOptions {
  maxPollingAttempts?: number;
  pollingIntervalMs?: number;
}

export function useRequestPolling(requestId: string, options: UseRequestPollingOptions = {}) {
  const {
    maxPollingAttempts = DEFAULT_MAX_POLLING_ATTEMPTS,
    pollingIntervalMs = DEFAULT_POLLING_INTERVAL_MS,
  } = options;

  const { refetch, data, isFetching, isError: fetchingError } = useFetchCompletion(requestId);

  const [pollingAttempts, setPollingAttempts] = useState(0);
  const [queuedPollsCount, setQueuedPollsCount] = useState(0);

  const queuedPollsRef = useRef(0);

  const shouldContinuePolling = (): boolean => {
    if (!data || !requestId) return false;

    const status = data.status;

    if (status === 'completed' || status === 'error' || fetchingError) {
      return false;
    }

    if (status === 'processing') {
      return pollingAttempts < maxPollingAttempts;
    }

    // For queued status, only poll a limited number of times as we expect it to transistion to SQS
    if (status === 'queued') {
      return queuedPollsRef.current < MAX_QUEUED_POLLS;
    }

    return false;
  };

  const shouldPoll = shouldContinuePolling();

  const pollingFunction = useCallback(() => {
    if (!requestId) return;

    setPollingAttempts(prev => prev + 1);

    // Track queued polls separately
    if (data?.status === 'queued') {
      queuedPollsRef.current += 1;
      setQueuedPollsCount(queuedPollsRef.current);
    }

    refetch();
  }, [requestId, data?.status, isFetching, refetch]);

  const { isPolling } = usePolling({
    shouldPoll,
    pollingFunction,
    interval: pollingIntervalMs,
    dependencies: [requestId, data, pollingAttempts, queuedPollsCount],
  });

  useEffect(() => {
    setPollingAttempts(0);
    setQueuedPollsCount(0);
    queuedPollsRef.current = 0;
  }, [requestId]);

  return {
    data,
    fetchingError,
    isPolling,
  };
}
